import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  PlusIcon,
  DocumentTextIcon,
  BuildingOffice2Icon,
  PencilSquareIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  ShareIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckIcon,
  EllipsisHorizontalIcon,
  ShoppingCartIcon,
  PaperClipIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import CommonPageHeader from '../common/CommonPageHeader';
import { useQuotations } from '../../hooks/useQuotations';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useCustomers } from '../../hooks/useCustomers';
import { useProducts } from '../../hooks/useProducts';
import LoadingScreen from '../common/LoadingScreen';
import { Quotation, QuotationStatus, CreateQuotationData } from '../../types/quotation';
import { QUOTATION_STATUS_META, normalizeStatus, quotationTotal, fmtMoney, fmtDate, toDate, isExpired, StatusSelect } from './quotationStatus';
import { Tooltip } from '../common/Tooltip';
import QuotationFormSimple from './QuotationFormSimple';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { generateHungarianPdf } from './generateHungarianPdf';
import Pagination from '../common/Pagination';
import ConfirmDeleteModal from 'components/common/ConfirmDeleteModal';
import DocumentViewerModal from 'components/documents/DocumentViewerModal';
import QuotationViewModal from './QuotationViewModal';
import ShareQuotationModal from './ShareQuotationModal';
import QuotationToOrderModal from './QuotationToOrderModal';
import { customerDisplayName } from '../../utils/customerName';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

interface QuotationListProps {
  onNewClick: () => void;
}

/** Teljes ajánlat betöltése Firestore-ból (dátumok konvertálva). */
const loadFullQuotation = async (id: string): Promise<Quotation | null> => {
  const snap = await getDoc(doc(db, 'quotations', id));
  if (!snap.exists()) return null;
  const data = snap.data();
  const conv = (v: any) => (v && typeof v === 'object' && 'toDate' in v ? v.toDate() : v);
  return {
    ...data,
    id,
    createdAt: data.createdAt?.toDate?.() || new Date(),
    updatedAt: data.updatedAt?.toDate?.() || new Date(),
    validUntil: conv(data.validUntil),
    date: conv(data.date),
    paymentDue: data.paymentDue,
    contractStart: conv(data.contractStart),
    contractEnd: conv(data.contractEnd),
    companyName: data.companyName || '',
    companyAddress: data.companyAddress || '',
    taxNumber: data.taxNumber || '',
    registrationNumber: data.registrationNumber || '',
    contactName: data.contactName || '',
    consumption: data.consumption || '',
    price: data.price || '',
    priceUnit: data.priceUnit || '',
    pod: data.pod || { id: '', address: '', measurementType: '', annualConsumption: '', power: '', profileNumber: '' },
    additionalPods: data.additionalPods || [],
    notes: data.notes || '',
    kat: data.kat || { kat: '', text: '' },
    items: data.items || [],
    documents: data.documents || []
  } as Quotation;
};

/* ---------- „Több” menü (fixed pozíció, hogy a táblázat ne vágja le) ---------- */
const MoreMenu: React.FC<{ items: { label: string; icon: React.ElementType; onClick: () => void; danger?: boolean }[] }> = ({ items }) => {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const close = () => setPos(null);
  const toggle = () => {
    if (pos) return close();
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = 192, height = items.length * 36 + 8;
    const top = r.bottom + height > window.innerHeight - 8 ? r.top - height - 4 : r.bottom + 4;
    setPos({ top, left: Math.max(8, r.right - width) });
  };
  useEffect(() => {
    if (!pos) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [pos]);
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); toggle(); }}
        className={`p-1.5 rounded-md hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white ${pos ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white' : ''}`}
        aria-label="További műveletek"
        aria-expanded={!!pos}
      >
        <EllipsisHorizontalIcon className="w-4 h-4" />
      </button>
      {pos && (
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: 192 }}
          className="z-50 rounded-lg border border-[var(--crm-border)] bg-[var(--crm-surface)] shadow-lg py-1"
          onClick={(e) => e.stopPropagation()}
        >
          {items.map((it, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { close(); it.onClick(); }}
              className={`w-full h-9 flex items-center gap-2 px-3 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700/60 ${it.danger ? 'text-rose-600 dark:text-rose-400' : 'text-gray-700 dark:text-gray-200'}`}
            >
              <it.icon className="w-4 h-4" />
              {it.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

/** Megnyitás-követés a listában: hányszor és mikor nyitotta meg az ügyfél a megosztott linket */
const ViewInfo: React.FC<{ q: Quotation }> = ({ q }) => {
  const ss: any = q.shareSettings;
  if (!ss) return null;
  const n = Number(ss.viewCount) || 0;
  const last = ss.lastViewed?.toDate ? ss.lastViewed.toDate() : ss.lastViewed ? new Date(ss.lastViewed) : null;
  if (n > 0) {
    return (
      <div className="text-[11px] text-emerald-700 dark:text-emerald-400 truncate" title={last ? `Utoljára megnyitva: ${last.toLocaleString('hu-HU')}` : undefined}>
        Megnyitva {n}×{last ? ` · ${last.toLocaleString('hu-HU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
      </div>
    );
  }
  return ss.sentAt ? <div className="text-[11px] text-amber-600 dark:text-amber-400">Elküldve, még nem nyitotta meg</div> : null;
};

const QuotationList: React.FC<QuotationListProps> = ({ onNewClick }) => {
  const {
    quotations,
    loading: quotationsLoading,
    deleteQuotation,
    duplicateQuotation,
    updateQuotation,
    refreshQuotations
  } = useQuotations();
  const { organizations, loading: orgsLoading } = useOrganizations();
  const { customers, loading: customersLoading } = useCustomers();
  const { products, loading: productsLoading } = useProducts();
  const { t } = useLanguage();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired'>('all');
  const [selectedQuotation, setSelectedQuotation] = useState<Quotation | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [selectedQuotations, setSelectedQuotations] = useState<string[]>([]);
  const [quotationsToDelete, setQuotationsToDelete] = useState<Quotation[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [selectedQuotationForView, setSelectedQuotationForView] = useState<Quotation | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [quotationToShare, setQuotationToShare] = useState<Quotation | null>(null);
  const [quotationToOrder, setQuotationToOrder] = useState<Quotation | null>(null);

  useEffect(() => {
    setSelectedQuotations([]);
    setCurrentPage(1);
  }, [searchTerm, selectedOrg, statusFilter]);

  const getCustomerName = (customerId: string) => {
    const c = customers?.find(x => x.id === customerId);
    return c ? customerDisplayName(c) : 'Ismeretlen ügyfél';
  };

  /* ---------- KPI-k ---------- */
  const stats = useMemo(() => {
    const all = quotations || [];
    const byOrg = selectedOrg === 'all' ? all : all.filter(q => q.organizationId === selectedOrg);
    const group = (k: 'draft' | 'sent' | 'accepted' | 'rejected') => byOrg.filter(q => normalizeStatus(q.status) === k);
    const sum = (list: Quotation[]) => list.reduce((s, q) => s + quotationTotal(q), 0);
    const draft = group('draft'), sent = group('sent'), accepted = group('accepted'), rejected = group('rejected');
    const expired = byOrg.filter(isExpired);
    const decided = accepted.length + rejected.length;
    return {
      all: { n: byOrg.length, v: sum(byOrg) },
      draft: { n: draft.length, v: sum(draft) },
      sent: { n: sent.length, v: sum(sent) },
      accepted: { n: accepted.length, v: sum(accepted) },
      rejected: { n: rejected.length, v: sum(rejected) },
      expired: { n: expired.length, v: sum(expired) },
      openValue: sum(draft) + sum(sent),
      acceptRate: decided ? Math.round((accepted.length / decided) * 100) : null
    };
  }, [quotations, selectedOrg]);

  const filteredQuotations = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (quotations || [])
      .filter(q => {
        const name = getCustomerName(q.customerId).toLowerCase();
        const searchMatch = !term
          || (q.quotationNumber || '').toLowerCase().includes(term)
          || (q.number || '').toLowerCase().includes(term)
          || name.includes(term);
        const orgMatch = selectedOrg === 'all' || q.organizationId === selectedOrg;
        const statusMatch = statusFilter === 'all'
          || (statusFilter === 'expired' ? isExpired(q) : normalizeStatus(q.status) === statusFilter);
        return searchMatch && orgMatch && statusMatch;
      })
      .sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotations, searchTerm, selectedOrg, statusFilter, customers]);

  const totalPages = Math.ceil(filteredQuotations.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentQuotations = filteredQuotations.slice(indexOfFirstItem, indexOfLastItem);

  /* ---------- Műveletek ---------- */
  const handleExportPDF = async (quotation: Quotation) => {
    // Csatolt (máshol elkészített) PDF: azt nyitjuk meg / töltjük le
    if (quotation.source === 'pdf' && quotation.attachedPdf?.url) {
      window.open(quotation.attachedPdf.url, '_blank', 'noopener');
      return;
    }
    try {
      const org = organizations.find(o => o.id === quotation.organizationId);
      const cust = customers.find(c => c.id === quotation.customerId);
      const quotationProducts = products.filter(p => quotation.items.some(item => item.productId === p.id));
      const pdf = await generateHungarianPdf(null, quotation, org, cust, quotationProducts);
      pdf.save(`${quotation.quotationNumber || quotation.number}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(t('quotations.messages.downloadError'));
    }
  };

  const handleDuplicate = async (quotation: Quotation) => {
    try {
      await duplicateQuotation(quotation.id);
      toast.success('Árajánlat duplikálva');
    } catch (error) {
      console.error('Error duplicating quotation:', error);
    }
  };

  const handleSetStatus = async (quotation: Quotation, status: QuotationStatus) => {
    if (status === quotation.status) return;
    try {
      await updateQuotation(quotation.id, { ...quotation, status });
      toast.success(`Státusz: ${QUOTATION_STATUS_META[status].label}`);
    } catch (error) {
      console.error('Error updating quotation status:', error);
      toast.error(t('common.messages.quotation.offerStatusUpdateFailed'));
    }
  };

  const handleShareQuotation = (quotation: Quotation) => {
    setQuotationToShare(quotation);
    setIsShareModalOpen(true);
  };

  const handlePreview = async (quotation: Quotation) => {
    try {
      const full = await loadFullQuotation(quotation.id);
      if (!full) { toast.error(t('common.messages.quotation.notFound')); return; }
      setSelectedQuotationForView(full);
      setIsDocumentViewerOpen(true);
    } catch (error) {
      console.error('Hiba az ajánlat dokumentumainak betöltése közben:', error);
      toast.error(t('common.messages.quotation.documentsLoadError'));
    }
  };

  const handleView = async (quotation: Quotation) => {
    try {
      const full = await loadFullQuotation(quotation.id);
      if (!full) { toast.error(t('common.messages.quotation.notFound')); return; }
      setSelectedQuotation(full);
      setIsViewModalOpen(true);
    } catch (error) {
      console.error('Hiba az ajánlat betöltése közben:', error);
      toast.error(t('common.messages.quotation.loadError'));
    }
  };

  const handleEdit = (quotation: Quotation) => {
    setSelectedQuotation(quotation);
    setIsEditOpen(true);
  };

  const handleBulkDelete = () => {
    setQuotationsToDelete(filteredQuotations.filter(q => selectedQuotations.includes(q.id)));
    setSelectedQuotation(null);
    setIsConfirmDeleteOpen(true);
  };

  const handleDelete = (quotation: Quotation) => {
    setSelectedQuotation(quotation);
    setQuotationsToDelete([quotation]);
    setIsConfirmDeleteOpen(true);
  };

  const allSelected = currentQuotations.length > 0 && currentQuotations.every(q => selectedQuotations.includes(q.id));
  const someSelected = selectedQuotations.length > 0 && !allSelected;
  const handleSelectAll = () => {
    if (allSelected) setSelectedQuotations([]);
    else setSelectedQuotations(currentQuotations.map(q => q.id));
  };
  const toggleSelect = (id: string) =>
    setSelectedQuotations(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  if (quotationsLoading || orgsLoading || customersLoading || productsLoading) {
    return <LoadingScreen />;
  }

  const moreItems = (q: Quotation) => [
    { label: 'Megrendelés készítése', icon: ShoppingCartIcon, onClick: () => setQuotationToOrder(q) },
    { label: 'Duplikálás', icon: DocumentDuplicateIcon, onClick: () => handleDuplicate(q) },
    { label: 'PDF letöltése', icon: DocumentArrowDownIcon, onClick: () => handleExportPDF(q) },
    { label: 'Csatolt dokumentumok', icon: PaperClipIcon, onClick: () => handlePreview(q) },
    { label: 'Törlés', icon: TrashIcon, onClick: () => handleDelete(q), danger: true }
  ];

  const kpiTiles: { key: typeof statusFilter; label: string; n: number; v: number; dot?: string }[] = [
    { key: 'all', label: 'Összes', n: stats.all.n, v: stats.all.v },
    { key: 'draft', label: 'Vázlat', n: stats.draft.n, v: stats.draft.v, dot: QUOTATION_STATUS_META.draft.dot },
    { key: 'sent', label: 'Elküldve', n: stats.sent.n, v: stats.sent.v, dot: QUOTATION_STATUS_META.sent.dot },
    { key: 'accepted', label: 'Elfogadva', n: stats.accepted.n, v: stats.accepted.v, dot: QUOTATION_STATUS_META.accepted.dot },
    { key: 'rejected', label: 'Elutasítva', n: stats.rejected.n, v: stats.rejected.v, dot: QUOTATION_STATUS_META.rejected.dot },
    { key: 'expired', label: 'Lejárt', n: stats.expired.n, v: stats.expired.v, dot: 'bg-amber-500' }
  ];

  const Checkbox: React.FC<{ checked: boolean; indeterminate?: boolean; onChange: () => void }> = ({ checked, indeterminate, onChange }) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onChange(); }}
      className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors
        ${checked ? 'bg-gray-900 border-gray-900 text-white dark:bg-white dark:border-white dark:text-gray-900'
          : indeterminate ? 'bg-gray-200 border-gray-400 dark:bg-gray-600 dark:border-gray-500'
          : 'border-gray-300 dark:border-gray-600 hover:border-gray-500'}`}
      aria-label="Kijelölés"
    >
      {checked && <CheckIcon className="w-3 h-3" />}
      {!checked && indeterminate && <span className="w-2 h-0.5 bg-gray-700 dark:bg-gray-200 rounded" />}
    </button>
  );

  return (
    <div className="space-y-4">
      <CommonPageHeader
        title={t('quotations.title')}
        subtitle={t('quotations.subtitle') || 'Ajánlatok kezelése és áttekintése'}
        icon={DocumentTextIcon}
      >
        <button onClick={onNewClick} className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors">
          <PlusIcon className="w-4 h-4 mr-2" />
          {t('quotations.new')}
        </button>
      </CommonPageHeader>

      {/* KPI csempék – kattintva szűrnek */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {kpiTiles.map(tile => (
          <button
            key={tile.key}
            onClick={() => setStatusFilter(tile.key)}
            className={`crm-card px-3 py-2 text-left transition-colors ${statusFilter === tile.key ? 'border-gray-900 dark:border-white' : 'hover:border-gray-300 dark:hover:border-gray-600'}`}
          >
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 min-w-0">
              {tile.dot && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${tile.dot}`} />}
              <span className="truncate">{tile.label}</span>
            </div>
            <div className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-50 leading-tight">{tile.n}</div>
            <div className="text-[11px] tabular-nums text-gray-500 dark:text-gray-400 truncate">{fmtMoney(tile.v)}</div>
          </button>
        ))}
      </div>

      {/* Szűrők + összegző */}
      <div className="crm-card p-3 sm:p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Keresés sorszám vagy ügyfél alapján…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-9 pl-9 pr-3 text-sm border rounded-lg"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <select
              value={selectedOrg}
              onChange={(e) => setSelectedOrg(e.target.value)}
              className="h-9 pl-3 pr-8 text-sm border rounded-lg max-w-[240px]"
            >
              <option value="all">Minden szervezet</option>
              {organizations?.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
          <div className="md:ml-auto flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
            <span>Nyitott érték: <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">{fmtMoney(stats.openValue)}</span></span>
            <span>Elfogadási arány: <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">{stats.acceptRate === null ? '–' : `${stats.acceptRate}%`}</span></span>
          </div>
        </div>
      </div>

      {/* Csoportos műveletek sáv */}
      {selectedQuotations.length > 0 && (
        <div className="crm-card px-3 py-2 flex items-center justify-between">
          <span className="text-sm text-gray-700 dark:text-gray-200">
            <span className="font-semibold">{selectedQuotations.length}</span> {t('quotations.selected')}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedQuotations([])} className="crm-btn crm-btn-ghost text-xs">Mégse</button>
            <button onClick={handleBulkDelete} className="crm-btn crm-btn-danger text-xs">
              <TrashIcon className="h-3.5 w-3.5 mr-1" />
              {t('quotations.bulkDelete')}
            </button>
          </div>
        </div>
      )}

      {/* Asztali táblázat */}
      <div className="hidden xl:block crm-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="w-10 pl-4 pr-2 py-2.5">
                  <Checkbox checked={allSelected} indeterminate={someSelected} onChange={handleSelectAll} />
                </th>
                <th className="text-left px-3 py-2.5">Árajánlat</th>
                <th className="text-left px-3 py-2.5">Ügyfél</th>
                <th className="text-left px-3 py-2.5">Szervezet</th>
                <th className="text-left px-3 py-2.5">Státusz</th>
                <th className="text-left px-3 py-2.5 whitespace-nowrap">Érvényes</th>
                <th className="text-right px-3 py-2.5 whitespace-nowrap">Bruttó összeg</th>
                <th className="w-32 px-3 py-2.5 text-right">Műveletek</th>
              </tr>
            </thead>
            <tbody>
              {currentQuotations.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <DocumentTextIcon className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                    <div className="text-sm text-gray-500 dark:text-gray-400">{t('quotations.noOffer')}</div>
                  </td>
                </tr>
              ) : currentQuotations.map(q => {
                const organization = organizations.find(o => o.id === q.organizationId);
                const expired = isExpired(q);
                const selected = selectedQuotations.includes(q.id);
                return (
                  <tr
                    key={q.id}
                    onDoubleClick={() => handleView(q)}
                    className={`group cursor-default ${selected ? 'bg-primary-50/60 dark:bg-primary-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'}`}
                  >
                    <td className="pl-4 pr-2 py-2.5">
                      <Checkbox checked={selected} onChange={() => toggleSelect(q.id)} />
                    </td>
                    <td className="px-3 py-2.5">
                      <button onClick={() => handleView(q)} className="text-left">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-50 hover:underline">
                          {q.quotationNumber || q.number}
                        </div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400">
                          {fmtDate(q.createdAt)}{q.items?.length ? ` · ${q.items.length} tétel` : ''}
                        </div>
                        <ViewInfo q={q} />
                      </button>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="text-sm text-gray-800 dark:text-gray-200 truncate max-w-[260px]">{getCustomerName(q.customerId)}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      {organization && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300 max-w-[180px]">
                          <BuildingOffice2Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{organization.name}</span>
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <StatusSelect value={q.status} onChange={(s) => handleSetStatus(q, s)} />
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 text-sm text-gray-800 dark:text-gray-200 tabular-nums">
                        {fmtDate(q.validUntil)}
                        {expired && (
                          <Tooltip content="Az ajánlat érvényessége lejárt">
                            <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />
                          </Tooltip>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-50">{fmtMoney(quotationTotal(q), q.currency || 'HUF')}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-0.5 text-gray-500 dark:text-gray-400">
                        <Tooltip content={t('quotations.tooltips.viewDetails')}>
                          <button onClick={() => handleView(q)} className="p-1.5 rounded-md hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white"><EyeIcon className="w-4 h-4" /></button>
                        </Tooltip>
                        <Tooltip content={t('quotations.tooltips.edit')}>
                          <button onClick={() => handleEdit(q)} className="p-1.5 rounded-md hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white"><PencilSquareIcon className="w-4 h-4" /></button>
                        </Tooltip>
                        <Tooltip content="Megosztás / küldés">
                          <button onClick={() => handleShareQuotation(q)} className="p-1.5 rounded-md hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white"><ShareIcon className="w-4 h-4" /></button>
                        </Tooltip>
                        <MoreMenu items={moreItems(q)} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredQuotations.length > 0 && (
          <div className="border-t border-[var(--crm-border)] px-3">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={filteredQuotations.length}
              indexOfFirstItem={indexOfFirstItem}
              indexOfLastItem={indexOfLastItem}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            />
          </div>
        )}
      </div>

      {/* Mobil / tablet kártyák */}
      <div className="xl:hidden space-y-3">
        {currentQuotations.length === 0 && (
          <div className="crm-card px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">{t('quotations.noOffer')}</div>
        )}
        {currentQuotations.map(q => {
          const organization = organizations.find(o => o.id === q.organizationId);
          const expired = isExpired(q);
          const selected = selectedQuotations.includes(q.id);
          return (
            <div
              key={q.id}
              role="button"
              tabIndex={0}
              onClick={() => handleView(q)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleView(q); }}
              className={`crm-card w-full overflow-hidden ${selected ? 'border-gray-900 dark:border-white' : ''}`}
            >
              <div className="p-3">
                <div className="flex items-start gap-2.5">
                  <div className="pt-0.5"><Checkbox checked={selected} onChange={() => toggleSelect(q.id)} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate">{q.quotationNumber || q.number}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-300 truncate">{getCustomerName(q.customerId)}</div>
                        <ViewInfo q={q} />
                      </div>
                      <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-50 whitespace-nowrap">{fmtMoney(quotationTotal(q), q.currency || 'HUF')}</div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                      <StatusSelect value={q.status} onChange={(s) => handleSetStatus(q, s)} size="xs" />
                      <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                        Érvényes: {fmtDate(q.validUntil)}
                        {expired && <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-500" />}
                      </span>
                      {organization && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400 min-w-0">
                          <BuildingOffice2Icon className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate max-w-[140px]">{organization.name}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="crm-card-action" onClick={(e) => e.stopPropagation()}>
                <button type="button" onClick={() => handleEdit(q)}><PencilSquareIcon /><span>Szerk.</span></button>
                <button type="button" onClick={() => handleShareQuotation(q)}><ShareIcon /><span>Küldés</span></button>
                <button type="button" onClick={() => handleExportPDF(q)}><DocumentArrowDownIcon /><span>PDF</span></button>
                <button type="button" onClick={() => handleDuplicate(q)}><DocumentDuplicateIcon /><span>Duplikál</span></button>
                <button type="button" onClick={() => handlePreview(q)}><PaperClipIcon /><span>Fájlok</span></button>
                <button type="button" onClick={() => handleDelete(q)} className="text-rose-600 dark:text-rose-400"><TrashIcon /><span>Törlés</span></button>
              </div>
            </div>
          );
        })}

        {filteredQuotations.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={filteredQuotations.length}
            indexOfFirstItem={indexOfFirstItem}
            indexOfLastItem={indexOfLastItem}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
          />
        )}
      </div>

      {/* Szerkesztés */}
      {selectedQuotation && (
        <QuotationFormSimple
          isOpen={isEditOpen}
          initialData={selectedQuotation}
          onSubmit={async (data: CreateQuotationData, opts?: { send?: boolean }) => {
            try {
              await updateQuotation(selectedQuotation.id, data);
              const merged = { ...selectedQuotation, ...data } as Quotation;
              setIsEditOpen(false);
              setSelectedQuotation(null);
              if (opts?.send) handleShareQuotation(merged);
            } catch (error) {
              console.error('Error updating quotation:', error);
            }
          }}
          onCancel={() => { setIsEditOpen(false); setSelectedQuotation(null); }}
        />
      )}

      {/* Törlés megerősítése */}
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => { setIsConfirmDeleteOpen(false); setSelectedQuotation(null); setQuotationsToDelete([]); }}
        onConfirm={() => {
          if (quotationsToDelete.length === 0) return;
          Promise.all(quotationsToDelete.map(q => deleteQuotation(q.id)))
            .then(() => {
              toast.success(quotationsToDelete.length > 1
                ? t('quotations.messages.bulkDeleted', { count: quotationsToDelete.length })
                : t('quotations.messages.deleted'));
              setSelectedQuotations([]);
            })
            .catch(error => {
              console.error('Error deleting quotations:', error);
              toast.error(t('quotations.messages.deleteError'));
            })
            .finally(() => { setIsConfirmDeleteOpen(false); setSelectedQuotation(null); setQuotationsToDelete([]); });
        }}
        title={quotationsToDelete.length > 1 ? t('quotations.bulkDeleteTitle') : t('quotations.messages.confirmTitle')}
        deleteMessage={quotationsToDelete.length > 1
          ? t('quotations.bulkDeleteMessage', { count: quotationsToDelete.length })
          : `${t('quotations.messages.deleteQuestion')} "${selectedQuotation?.quotationNumber || selectedQuotation?.number || ''}"?`}
        deleteWarning={quotationsToDelete.length > 1 ? t('quotations.bulkDeleteWarning') : t('quotations.messages.deleteWarning')}
      />

      {/* Csatolt dokumentumok */}
      {selectedQuotationForView && (
        <DocumentViewerModal
          isOpen={isDocumentViewerOpen}
          onClose={() => { setIsDocumentViewerOpen(false); setSelectedQuotationForView(null); }}
          customerWithDocuments={selectedQuotationForView}
        />
      )}

      {/* Részletek */}
      {selectedQuotation && (
        <QuotationViewModal
          isOpen={isViewModalOpen}
          onClose={() => { setIsViewModalOpen(false); setSelectedQuotation(null); }}
          quotation={selectedQuotation}
          onEdit={(q) => { setIsViewModalOpen(false); setSelectedQuotation(q); setIsEditOpen(true); }}
          onShare={(q) => { setIsViewModalOpen(false); setSelectedQuotation(null); handleShareQuotation(q); }}
          onExportPdf={handleExportPDF}
          onStatusChange={async (q, s) => { await handleSetStatus(q, s); setSelectedQuotation({ ...q, status: s }); }}
          onCreateOrder={(q) => { setIsViewModalOpen(false); setSelectedQuotation(null); setQuotationToOrder(q); }}
        />
      )}

      {quotationToOrder && (
        <QuotationToOrderModal
          isOpen
          onClose={() => setQuotationToOrder(null)}
          quotation={quotationToOrder}
          onCreated={() => refreshQuotations()}
        />
      )}

      <ShareQuotationModal
        isOpen={isShareModalOpen}
        onClose={() => { setIsShareModalOpen(false); refreshQuotations(); }}
        quotation={quotationToShare}
      />
    </div>
  );
};

export default QuotationList;
